// components/Avatar/index.tsx
import React, { memo, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase-client";
import { AvatarBase } from "@/components/Avatar/Base";
import { tryGravatar } from "@/lib/gravatar";
import useAuth from "@/hooks/useAuth";

/* session caches */
const PROFILE_CACHE = new Map<string, { first_name?: string; last_name?: string }>();
const PENDING_AVATAR_REQUESTS = new Map<string, Promise<string | null>>();

/* cache management - exported for use by services */
export function clearAvatarCache(userId: string) {
  AVATAR_URL_CACHE.delete(userId);
}

/* tiny helpers */
const palette = [
  "#EF4444",
  "#F97316",
  "#F59E0B",
  "#84CC16",
  "#22C55E",
  "#14B8A6",
  "#06B6D4",
  "#3B82F6",
  "#6366F1",
  "#8B5CF6",
  "#A855F7",
  "#D946EF",
  "#EC4899",
  "#F43F5E",
  "#10B981",
  "#0EA5E9",
  "#EAB308",
  "#D97706",
  "#059669",
  "#2563EB",
];
const colorOf = (seed: string) => palette[[...seed].reduce((a, c) => a + c.charCodeAt(0), 0) % palette.length];
const initialsOf = (f?: string, l?: string) =>
  `${(f || "").slice(0, 1).toUpperCase()}${(l || "").slice(0, 1).toUpperCase()}`;

// Cache includes expiry time for signed URLs
const SIGNED_URL_EXPIRY = 3600; // 1 hour
const CACHE_BUFFER = 300; // Refresh 5 min before expiry

interface CachedUrl {
  url: string;
  expiresAt: number;
}

const AVATAR_URL_CACHE = new Map<string, CachedUrl>();

function getCachedUrl(userId: string): string | null {
  const cached = AVATAR_URL_CACHE.get(userId);
  if (!cached) return null;
  // Return null if expired or about to expire
  if (Date.now() > cached.expiresAt - CACHE_BUFFER * 1000) {
    AVATAR_URL_CACHE.delete(userId);
    return null;
  }
  return cached.url;
}

function setCachedUrl(userId: string, url: string | null) {
  if (url) {
    AVATAR_URL_CACHE.set(userId, {
      url,
      expiresAt: Date.now() + SIGNED_URL_EXPIRY * 1000,
    });
  } else {
    // Cache null results for a shorter time (1 minute) to avoid repeated lookups
    AVATAR_URL_CACHE.set(userId, {
      url: "",
      expiresAt: Date.now() + 60 * 1000,
    });
  }
}

async function findAvatarInBucket(userId: string, tenantId?: string): Promise<string | null> {
  // Return cached result if still valid
  const cached = getCachedUrl(userId);
  if (cached !== null) return cached || null;

  // Return pending request if one exists (deduplication for concurrent calls)
  if (PENDING_AVATAR_REQUESTS.has(userId)) {
    return PENDING_AVATAR_REQUESTS.get(userId)!;
  }

  // Create new request and track it
  const request = (async (): Promise<string | null> => {
    // Get tenant_id if not provided
    let tenant_id = tenantId;
    if (!tenant_id) {
      // Use maybeSingle() to avoid 406 errors when user not found
      const { data: userData } = await supabase.from("users").select("tenant_id").eq("id", userId).maybeSingle();
      tenant_id = userData?.tenant_id;
    }

    if (!tenant_id) {
      setCachedUrl(userId, null);
      return null;
    }

    try {
      // List files in avatars bucket under tenant/users folder that match userId pattern
      const { data: files } = await supabase.storage.from("avatars").list(`${tenant_id}/users`, {
        search: userId,
      });

      if (files && files.length > 0) {
        // Found a matching file, get a signed URL
        const file = files[0];
        const { data, error } = await supabase.storage
          .from("avatars")
          .createSignedUrl(`${tenant_id}/users/${file.name}`, SIGNED_URL_EXPIRY);

        if (!error && data?.signedUrl) {
          setCachedUrl(userId, data.signedUrl);
          return data.signedUrl;
        }
      }
    } catch {
      // If list fails, fall through to null
    }

    setCachedUrl(userId, null);
    return null;
  })();

  PENDING_AVATAR_REQUESTS.set(userId, request);

  try {
    return await request;
  } finally {
    PENDING_AVATAR_REQUESTS.delete(userId);
  }
}

async function findGravatarByEmail(email: string, size = 80, useIdenticon = false) {
  // tryGravatar handles TTL-based caching internally
  return tryGravatar(email, size, useIdenticon);
}

async function findAvatar(userId: string, userEmail?: string, size = 80, useIdenticon = false): Promise<string | null> {
  // 1. Try storage bucket first
  const storageUrl = await findAvatarInBucket(userId);
  if (storageUrl) return storageUrl;

  // 2. Try gravatar if email available
  if (userEmail) {
    const gravatarUrl = await findGravatarByEmail(userEmail, size, useIdenticon);
    if (gravatarUrl) return gravatarUrl;
  }

  // 3. Fall back to initials (handled by caller)
  return null;
}

type Props = {
  userId?: string; // primary lookup
  email?: string; // for gravatar lookup
  firstName?: string | null; // fallback only
  lastName?: string | null; // fallback only
  size?: number;
  fontSize?: number;
  style?: any;
  useIdenticon?: boolean; // If true, use Gravatar's identicon pattern as fallback instead of colored initials
};

const Avatar = memo(function Avatar({
  userId,
  email: emailProp,
  firstName: fnProp,
  lastName: lnProp,
  useIdenticon = false,
  ...baseProps
}: Props) {
  const { user } = useAuth();
  const [profile, setProfile] = useState<{ first_name?: string; last_name?: string }>({});
  const [uri, setUri] = useState<string | null>(null);

  // Load profile names and avatar by userId
  useEffect(() => {
    let cancel = false;

    (async () => {
      if (!userId) {
        setProfile({});
        setUri(null);
        return;
      }

      // Load profile data (cached)
      if (!PROFILE_CACHE.has(userId)) {
        const { data, error } = await supabase
          .from("profiles")
          .select("first_name, last_name, preferred_name")
          .eq("id", userId)
          .maybeSingle();

        if (!error) PROFILE_CACHE.set(userId, { first_name: data?.first_name, last_name: data?.last_name });
      }

      if (cancel) return;

      setProfile(PROFILE_CACHE.get(userId) ?? {});

      // Determine email for gravatar lookup
      let userEmail = emailProp;

      // If no email provided and this is the current user, use their email
      if (!userEmail && user?.id === userId) {
        userEmail = user.email;
      }

      // Find avatar with full fallback chain
      const url = await findAvatar(userId, userEmail, baseProps.size, useIdenticon);
      if (!cancel) setUri(url);
    })();

    return () => {
      cancel = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, emailProp, user?.email, baseProps.size, useIdenticon]);

  // Prefer DB names; fall back to props
  const f = profile.first_name ?? fnProp ?? "";
  const l = profile.last_name ?? lnProp ?? "";

  const initials = useMemo(() => initialsOf(f, l) || " ", [f, l]);
  const bg = useMemo(() => colorOf(f + l), [f, l]);

  return <AvatarBase initials={initials} uri={uri} backgroundColor={bg} {...baseProps} />;
});

export default Avatar;

// Backward compatibility exports
export { Avatar as Profile };
export { Avatar as ProfilePic };
